'use client';

import { Button, Divider, useDisclosure } from '@nextui-org/react';
import moment from 'moment';
import Link from 'next/link';
import React from 'react';

import { ArtworkRelated, BannerPromptDetail, SChip } from '@/components';
import ButtonViewMore from '@/components/ButtonViewMore';
import RenderWhen from '@/components/RenderWhen';
import useViewMore from '@/hooks/useViewMore';
import { useLayout } from '@/provider/LayoutProvider';
import { useAuth } from '@/provider/UserProvider';
import type { Prompt } from '@/types/prompt';
import { convertRoyaltyFromContract } from '@/utils';
import { currencyFormatter } from '@/utils/currency';

import BuyPrompt from './BuyPrompt';
import Checkout from './Checkout';
import TooltipCustom from '@/components/tooltip-custom';

const DetailPrompt: React.FC<{
  prompt: Prompt;
}> = ({ prompt }) => {
  const { toggle, isShowBtn, isViewMore } = useViewMore(120, 'view-more-desc');

  const { auth } = useAuth();
  const { setIsLoadingBar } = useLayout();
  const isOwner = auth?.user?.publicAddress === prompt?.asset?.offer?.sellerAddress;
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const {
    isOpen: isOpenCheckout,
    onClose: onCloseCheckout,
    onOpen: onOpenCheckout,
  } = useDisclosure();

  return (
    <div className="main-container mt-10">
      <div className="flex items-start md:flex-col md:flex-wrap">
        <BannerPromptDetail prompt={prompt} />
        <div className="w-[55%] pl-[80px] lg:pl-3 md:w-full md:!pr-0">
          <h1 className="m-0 text-[40px] font-bold leading-[150%] lg:text-[28px] md:!text-[22px]">
            {prompt.name || 'Unnamed prompt'}
            <span className="ml-3 text-gray-500">#{prompt?.tokenId}</span>
          </h1>
          <span className="mt-[5px] text-[14px] font-normal text-gray-500">
            Created by{' '}
            <Link
              href={`/profile/${prompt.creatorProvider?.publicAddress}`}
              onClick={() => setIsLoadingBar(true)}
              className="ml-1 text-[16px] font-semibold text-orange-500 sm:text-[14px]"
            >
              {prompt.creatorProvider?.username || 'Unname'}
            </Link>
          </span>
          <div className="mt-[35px] flex items-end justify-between">
            <div className="flex items-center">
              {[
                {
                  title: 'Word count',
                  desc: `${
                    prompt.numberOfWords > 1
                      ? `${prompt.numberOfWords} Words`
                      : `${prompt.numberOfWords} Word`
                  }`,
                },
                // { title: 'Artwork ID', desc: `#${prompt?.asset?.id}` },
              ].map((item, index) => (
                <div key={index} className="mr-8 flex flex-col sm:mr-4">
                  <div className="mb-3 text-[14px] font-semibold text-gray-600 sm:text-[13px]">
                    {item.title}
                  </div>
                  <SChip className="rounded-[100px] bg-gray-50 px-[16px] py-[10px] text-[16px] font-normal text-[black] sm:py-[6px] sm:text-[14px]">
                    {item.desc}
                  </SChip>
                </div>
              ))}
            </div>
          </div>
          {prompt?.asset?.offer?.offeringId ? (
            <div className="mt-[35px] max-w-[300px] rounded-[27px] border border-gray-100 p-6">
              <span className="mb-4 text-[16px] font-semibold text-gray-500 sm:text-[14px]">
                Current price
              </span>
              <p className="text-[40px] font-bold sm:text-[28px]">
                ${currencyFormatter(prompt?.asset?.offer?.amount)}
              </p>
              <div className="mt-8 flex items-center justify-between sm:mt-5">
                <RenderWhen>
                  <RenderWhen.If isTrue={isOwner}>
                    <TooltipCustom content="You are owner">
                      <Button
                        size="lg"
                        className="mr-3 w-full max-w-[250px] cursor-default rounded-[100px] !bg-gray-200 sm:mr-0"
                        disabled
                      >
                        <span className="text-[18px] font-medium text-white sm:text-[16px]">
                          Buy prompt
                        </span>
                      </Button>
                    </TooltipCustom>
                  </RenderWhen.If>
                  <RenderWhen.If isTrue={!isOwner}>
                    <Button
                      size="lg"
                      className="mr-3 w-full max-w-[250px] rounded-[100px] bg-primary sm:mr-0"
                      onClick={onOpen}
                    >
                      <span className="text-[18px] font-medium text-white sm:text-[16px]">
                        Buy prompt
                      </span>
                    </Button>
                  </RenderWhen.If>
                </RenderWhen>
              </div>
            </div>
          ) : (
            <div className="mt-10 inline-block rounded-[100px] bg-gray-50 px-6 py-4 text-[16px] font-semibold">
              This prompt has not been set for purchase.
            </div>
          )}
          {!prompt?.asset?.offer?.amount && (
            <>
              <Divider className="my-10 bg-gray-100" />
              <div className="mt-10">
                <div className="mb-1 text-[24px] font-semibold text-black lg:text-[20px] sm:!text-[18px]">
                  Prompt
                </div>
                <div className="rounded-xl bg-gray-50 p-3">
                  <div
                    id="view-more-desc"
                    className="font-mono text-[15px] leading-6 text-[#505665] [overflow-wrap:anywhere]"
                  >
                    {prompt?.prompt}
                  </div>
                </div>
                <ButtonViewMore
                  toggle={toggle}
                  isViewMore={isViewMore}
                  isShowBtn={isShowBtn}
                  className="mt-4"
                />
              </div>
            </>
          )}
          <Divider className="my-10 bg-gray-100" />
          <div className="mt-10">
            <div className="mb-1 text-[24px] font-semibold text-black lg:text-[20px] sm:!text-[18px]">
              Description
            </div>
            <div
              id="view-more-desc"
              className="text-[14px] leading-[1.7] text-[#505665] [overflow-wrap:anywhere]"
            >
              {prompt?.asset?.description}
            </div>
            <ButtonViewMore
              toggle={toggle}
              isViewMore={isViewMore}
              isShowBtn={isShowBtn}
              className="mt-4"
            />
          </div>
          <Divider className="my-10 bg-gray-100" />
          <div className="flex flex-col items-start gap-2 self-stretch">
            <p className="text-[24px] font-semibold lg:text-[18px]">Details</p>
            <div className="flex flex-col items-start gap-3 self-stretch">
              {[
                {
                  label: "Creator's royalty",
                  desc: `${convertRoyaltyFromContract(prompt?.asset?.generatorRoyalty || 0)}%`,
                },
                { label: 'Date created', desc: moment(prompt?.createdAt).format('ll') },
                { label: 'Prompt ID', desc: `#${prompt?.tokenId}` },
              ].map((item) => {
                const { label, desc } = item;
                return (
                  <div className="flex items-center justify-between self-stretch" key={label}>
                    <p className="text-14 font-normal">{label}</p>
                    <p className="text-14 font-semibold text-gray-600">{desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <Divider className="my-15 bg-gray-100 lg:my-10" />
      <ArtworkRelated promptId={prompt?.id} />
      <BuyPrompt
        id={prompt?.id}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        onOpenCheckout={onOpenCheckout}
      />
      <Checkout
        isOpen={isOpenCheckout}
        onClose={onCloseCheckout}
        id={prompt?.id}
        onOpenPayment={onOpen}
      />
    </div>
  );
};

export default DetailPrompt;
